// SolyticsTableFilter.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTheme, Skeleton } from '@mui/material';
import { ReactComponent as StarIcon } from '../../assets/Icons/lucideIcons/star.svg';
import { ReactComponent as EditOutlinedIcon } from '../../assets/Icons/lucideIcons/pencil.svg';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { ReactComponent as TrashIcon } from '../assets/TrashIcon.svg';
import { ReactComponent as PencilIcon } from '../assets/PencilIcon.svg';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsSearchBar from './SolyticsSearchBar';
import SolyticsInput from './SolyticsInput';
import SolyticsAutocomplete from './SolyticsAutocomplete';
import SolyticsTabs from './SolyticsTabs';
import SolyticsButton from './SolyticsButton';
import SolyticsChip from './SolyticsChip';
import SolyticsLabel from './SolyticsLabel';
import SolyticsModal from './SolyticsModal';
import SolyticsAdvancedFilter from './SolyticsAdvancedFilter';
import SolyticsIconButton from './SolyticsIconButton';
import SolyticsTooltip from './SolyticsTooltip';
import SolyticsList from './SolyticsList';
import SolyticsGrid from './SolyticsGrid';
import SolyticsStack from './SolyticsStack';
import SolyticsDrawer from './SolyticsDrawer';

// ---------------------------------------------------------------------------
// Tab index constants
// ---------------------------------------------------------------------------
const TAB = {
  BASIC: 0,
  ADVANCED: 1,
  SAVED: 2,
};

const TAB_LABELS = {
  [TAB.BASIC]: 'Basic',
  [TAB.ADVANCED]: 'Advanced',
  [TAB.SAVED]: 'Saved',
};

const FILTER_TYPE_LABELS = {
  basic: 'Basic',
  advanced: 'Advanced',
};

// ---------------------------------------------------------------------------
// Default advanced criteria
// ---------------------------------------------------------------------------
const DEFAULT_ADVANCED_CRITERIA = { logic: 'AND', rules: [], sort: null };

// ---------------------------------------------------------------------------
// Selected saved filter highlight colour
// ---------------------------------------------------------------------------
const SELECTED_FILTER_BG = 'rgba(12, 50, 112, 0.06)';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isValueEmpty = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const flattenRules = (rules = []) => {
  const arr = Array.isArray(rules) ? rules : rules?.rules || [];
  return (arr || []).flatMap((rule) => {
    if (!rule) return [];
    if (Array.isArray(rule.rules)) return flattenRules(rule.rules);
    return [rule];
  });
};

const tabHasCriteria = (
  tab,
  basicCriteria,
  advancedCriteria,
  selectedSavedFilterId,
  hasSavedFilterContext
) => {
  if (tab === TAB.BASIC) {
    return Object.values(basicCriteria).some((v) => !isValueEmpty(v));
  }
  if (tab === TAB.ADVANCED) {
    const rules = flattenRules(advancedCriteria?.rules || []);
    return (
      rules.some((r) => !isValueEmpty(r.value)) ||
      Boolean(advancedCriteria?.sort)
    );
  }
  if (tab === TAB.SAVED) {
    return Boolean(selectedSavedFilterId) || Boolean(hasSavedFilterContext);
  }
  return false;
};

// ---------------------------------------------------------------------------
// EditingBanner
// ---------------------------------------------------------------------------
const EditingBanner = ({ filterName, onEditMetadata, theme }) => (
  <SolyticsBox
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
    mr={2}
    mb={2}
    px={2}
    py={1.25}
    borderRadius="8px"
    borderLeft={`3px solid ${theme.palette.primary.main}`}
    bgcolor={SELECTED_FILTER_BG}
    flexShrink={0}
  >
    <SolyticsBox display="flex" alignItems="center" gap={0.5} overflow="hidden">
      <SolyticsTypography variant="body2">Editing:</SolyticsTypography>
      <SolyticsTypography variant="body2" noWrap ellipsis>
        {filterName}
      </SolyticsTypography>
    </SolyticsBox>

    <SolyticsBox flexShrink={0}>
      <SolyticsButton
        size="small"
        variant="outlined"
        startIcon={<EditOutlinedIcon fontSize="small" />}
        onClick={onEditMetadata}
      >
        Edit name & description
      </SolyticsButton>
    </SolyticsBox>
  </SolyticsBox>
);

// ---------------------------------------------------------------------------
// SolyticsTableFilter
// ---------------------------------------------------------------------------

/**
 * SolyticsTableFilter
 *
 * The product-aware container for the complete filter experience.
 * Assembles three fixed tabs — [Basic Filters] [Advanced Filters] [Saved Filters] —
 * into a right-anchored Drawer, wiring them together with shared state, confirmation
 * dialogs, and a unified output contract.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE-LEVEL ARCHITECTURE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   Level 1 — Primitives:  SolyticsInput · SolyticsDropdownInput · SolyticsAutocomplete
 *   Level 2 — Mechanism:   SolyticsAdvancedFilter  (pure query builder, JSON in/out)
 *   Level 3 — Feature:     SolyticsTableFilter     (this component)
 *
 * This is the only level allowed to know application domain context. It translates
 * product props (entityOptions, roleList, showAdvancedRuleButton) into the generic
 * schema SolyticsAdvancedFilter understands. The consumer never interacts with
 * SolyticsAdvancedFilter directly.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * UNIFIED onApply OUTPUT — always one shape
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Fires on every explicit user commit, regardless of which tab was active or
 * whether the filter came from manual input or a saved filter:
 *
 * ```js
 * {
 *   mode:        'basic' | 'advanced',
 *   logic:       'and' | 'or',          // always lowercase
 *   rules:       Array,
 *   sort:        null | { fieldId, order: 'asc'|'desc'|null, dataType: string|null },
 *   savedFilter: null | { id: string, name: string },
 *   // Non-null only when a saved filter was applied or updated.
 *   // Always null for manually built filters.
 *   // SolyticsAppliedFilterCard reads this key for chip label and checked-state.
 * }
 * ```
 *
 * The consumer transforms this output to their backend contract.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIVE CALLBACKS vs APPLY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   onBasicFilterChange / onAdvancedFilterChange — fired on every change (live).
 *     For consumers that need to react to in-progress edits (URL sync, preview).
 *     Optional — most consumers only need onApply.
 *
 *   onApply — fired only when the user explicitly commits. Primary integration point.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAVED FILTER PERSISTENCE — component fires, consumer owns
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   onSaveFilter(filterData)          → consumer calls POST /api/filters
 *   onDeleteFilter(id)                → consumer calls DELETE /api/filters/:id
 *   onUpdateFilter(id, filterData)    → consumer calls PUT /api/filters/:id
 *   onToggleFavorite(id)              → consumer calls PATCH /api/filters/:id
 *
 * The component renders whatever `savedFilters` it receives. It never fetches
 * or caches independently. Saved filter criteria shape:
 *   type 'basic'    → criteria: { [fieldId]: value }
 *   type 'advanced' → criteria: { logic, rules, sort }  ← sort is INSIDE criteria
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONFIRMATION DIALOGS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Three scenarios guarded by confirmation:
 *   1. Tab switch when Basic/Advanced has unsaved criteria
 *   2. Tab switch away from Saved tab with a filter selected
 *   3. Close (✕) while a saved filter is being edited (EditingBanner visible)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OPENING PRE-POPULATED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Derive and pass back so the drawer opens showing the currently applied filter:
 *
 * ```js
 * const basicFilterCriteria = appliedFilter?.mode === 'basic'
 *   ? Object.fromEntries(appliedFilter.rules.map(r => [r.field, r.value]))
 *   : {};
 *
 * const advancedFilterCriteria = appliedFilter?.mode === 'advanced'
 *   ? { logic: appliedFilter.logic.toUpperCase(), rules: appliedFilter.rules, sort: appliedFilter.sort }
 *   : null;
 *
 * const defaultSavedFilterId = appliedFilter?.savedFilter?.id ?? null;
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REF API
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ```jsx
 * const filterRef = useRef();
 * <SolyticsTableFilter ref={filterRef} ... />
 *
 * filterRef.current.clearFilter()
 * // Resets all internal state (criteria, editingFilter, selectedSavedFilterId).
 * // Also fires onBasicFilterChange({}), onAdvancedFilterChange(DEFAULT), onReset().
 * // Call this when clearing appliedFilter from outside the drawer.
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @param {boolean}   props.open                           Drawer open state.
 * @param {Function}  props.onClose                        Called when drawer should close.
 * @param {string}    [props.title='All Filters']           Drawer header title.
 *
 * @param {Array}     [props.basicFilterFields=[]]
 *   Basic tab field definitions.
 *   Shape: { id, label, selectionMode: 'text'|'multiselect'|'single', options?, getValueGetter?, formatOptions? }
 *
 * @param {Array}     [props.advancedFilterFields=[]]
 *   Advanced tab simple field definitions. Do NOT include isGroup here —
 *   group fields are constructed internally from entityOptions + roleList.
 *   Shape: { id, label, operators: [{ value, label }], getValueGetter?, formatOptions? }
 *
 * @param {Object}    [props.basicFilterCriteria={}]
 *   Initial basic criteria on open: { [fieldId]: value }
 *   Pass the value derived from appliedFilter to open pre-populated.
 *
 * @param {Object}    [props.advancedFilterCriteria=null]
 *   Initial advanced criteria on open: { logic, rules, sort }
 *   Matches SolyticsAdvancedFilter value schema exactly.
 *
 * @param {Function}  [props.onBasicFilterChange]
 *   Live callback fired on every basic field change. Signature: (criteria) => void.
 *
 * @param {Function}  [props.onAdvancedFilterChange]
 *   Live callback fired on every advanced rule change. Signature: (criteria) => void.
 *
 * @param {Function}  [props.onApply]
 *   Commit event. Fires the unified output shape on explicit user action.
 *
 * @param {Function}  [props.onReset]
 *   Called when the user clicks Reset or clearFilter() is invoked via ref.
 *
 * @param {Function}  [props.onValidationWarning]
 *   Called when the user tries to Apply with empty rule values in Advanced mode.
 *   Signature: (message: string) => void  — consumer decides how to surface the warning.
 *
 * @param {Array}     [props.savedFilters=[]]
 *   Saved filter list. Consumer owns persistence; this component only renders.
 *   Shape: { id, name, description, type: 'basic'|'advanced', isFavorite, criteria }
 *
 * @param {Function}  [props.onSaveFilter]    async (filterData) => void
 * @param {Function}  [props.onDeleteFilter]  async (id) => void
 * @param {Function}  [props.onUpdateFilter]  async (id, filterData) => void
 * @param {Function}  [props.onToggleFavorite] async (id) => void
 *
 * @param {string}    [props.defaultSavedFilterId=null]
 *   Opens the drawer on the Saved Filters tab with this filter pre-selected.
 *   Pass appliedFilter?.savedFilter?.id ?? null to keep the drawer in sync.
 *
 * @param {boolean}   [props.showBasicFilters=true]       Show the Basic Filters tab.
 * @param {boolean}   [props.showSavedFilters=true]       Show the Saved Filters tab.
 * @param {boolean}   [props.showAdvancedRuleButton=true]
 *   Show the "Add Advanced Rule" button in the query builder.
 *   Consumers who previously used isTemporalView should pass
 *   showAdvancedRuleButton={!isTemporalView} at the call site.
 *
 * @param {Array}     [props.entityOptions=[]]
 *   Entities available as group rules in advanced mode.
 *   Shape: { value: string, label: string }
 *   Each entity becomes a group field in the SolyticsAdvancedFilter schema.
 *   The 'Team' entity uses roleList as its nested fields instead of advancedFilterFields.
 *
 * @param {Function}  [props.filterEntities]
 *   Optional fn to filter visible entities before schema construction.
 *   Signature: (entities: Array) => Array
 *
 * @param {Array}     [props.roleList=[]]
 *   Role definitions used as nested fields when the 'Team' entity is selected.
 *   Shape: { value: string, label: string }
 *
 * @param {Array}     [props.sortableFields=[]]
 *   Fields available for sort. Passed through to SolyticsAdvancedFilter.
 *   Shape: { id: string, label: string, dataType: string }
 *   Empty = no Sort Order button.
 *
 * @param {Array}     [props.dataTypeOptions=[]]
 *   Data type filter options for the sort row. Passed through to SolyticsAdvancedFilter.
 *   Shape: { value: string, label: string }
 *
 * @param {boolean}   [props.loading=false]
 *   Renders Skeleton placeholders while field/entity data is fetching.
 *
 * @param {Object}    [props.sx]
 *   Additional MUI sx applied to the Drawer Paper.
 */
const SolyticsTableFilter = forwardRef(
  (
    {
      open,
      onClose,
      title = 'All Filters',
      basicFilterFields = [],
      advancedFilterFields = [],
      basicFilterCriteria = {},
      advancedFilterCriteria = null,
      onBasicFilterChange,
      onAdvancedFilterChange,
      onApply,
      onReset,
      onValidationWarning,
      savedFilters = [],
      onSaveFilter,
      onDeleteFilter,
      onUpdateFilter,
      onToggleFavorite,
      defaultSavedFilterId = null,
      showBasicFilters = true,
      showSavedFilters = true,
      showAdvancedRuleButton = true,
      entityOptions = [],
      filterEntities,
      roleList = [],
      sortableFields = [],
      dataTypeOptions = [],
      loading = false,
    },
    ref
  ) => {
    const theme = useTheme();

    const initialTab = useMemo(
      () => (!showBasicFilters ? TAB.ADVANCED : TAB.BASIC),
      [showBasicFilters]
    );

    const [activeTab, setActiveTab] = useState(initialTab);

    // -----------------------------------------------------------------------
    // Confirmation dialog
    // -----------------------------------------------------------------------
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmContext, setConfirmContext] = useState(null);

    // -----------------------------------------------------------------------
    // Delete confirmation dialog
    // -----------------------------------------------------------------------
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [filterToDelete, setFilterToDelete] = useState(null);

    // -----------------------------------------------------------------------
    // hasSavedFilterContext
    // True when the drawer was opened while a saved filter was applied
    // (i.e. defaultSavedFilterId was non-null on open). This makes the Saved
    // tab trigger the confirmation even when selectedSavedFilterId is null —
    // covering the "Create New Filter" from popper scenario where a saved
    // filter chip is shown outside but no row is re-selected inside yet.
    // -----------------------------------------------------------------------
    const [hasSavedFilterContext, setHasSavedFilterContext] = useState(false);

    // -----------------------------------------------------------------------
    // Internal filter state
    // -----------------------------------------------------------------------
    const [internalBasicCriteria, setInternalBasicCriteria] = useState({});
    const [internalAdvancedCriteria, setInternalAdvancedCriteria] = useState(
      DEFAULT_ADVANCED_CRITERIA
    );

    // -----------------------------------------------------------------------
    // Save / edit dialog state
    // -----------------------------------------------------------------------
    const [saveFilterDialogOpen, setSaveFilterDialogOpen] = useState(false);
    const [saveFilterName, setSaveFilterName] = useState('');
    const [saveFilterDescription, setSaveFilterDescription] = useState('');
    const [editingFilter, setEditingFilter] = useState(null);

    // -----------------------------------------------------------------------
    // Saved filters UI state
    // -----------------------------------------------------------------------
    const [savedFiltersSearchQuery, setSavedFiltersSearchQuery] = useState('');
    const [selectedSavedFilterId, setSelectedSavedFilterId] = useState(null);

    // -----------------------------------------------------------------------
    // Basic filter UI state
    // -----------------------------------------------------------------------
    const [selectedBasicField, setSelectedBasicField] = useState(null);
    const [basicFieldSearchQuery, setBasicFieldSearchQuery] = useState('');

    // -----------------------------------------------------------------------
    // Dynamic skeleton counts based on viewport height
    // -----------------------------------------------------------------------
    const skeletonCounts = useMemo(() => {
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
      // Approximate overhead: header(56) + horizontal tabs(48) + footer(64) + padding(80)
      const contentHeight = vh - 250;

      // Left column: skeleton height 40 + gap 16 = 56 per item
      const leftColumn = Math.max(3, Math.floor(contentHeight / 56));
      // Right column: search bar ~46px, then skeleton height 40 + gap 8 = 48 per item
      const rightColumn = Math.max(3, Math.floor((contentHeight - 46) / 48));
      // Saved filters: search bar ~40px, then skeleton height 68 + gap 8 = 76 per item
      // const savedFilters = Math.max(2, Math.floor((contentHeight - 40) / 76));

      return { leftColumn, rightColumn, savedFilters };
    }, []);

    // -----------------------------------------------------------------------
    // Advanced filter fields schema
    // -----------------------------------------------------------------------
    const advancedFilterFieldsSchema = useMemo(() => {
      const simpleFields = advancedFilterFields.map((f) => ({
        id: f.id,
        label: f.label,
        operators: f.operators || [],
        ...(f.getValueGetter ? { getValueGetter: f.getValueGetter } : {}),
        ...(f.formatOptions ? { formatOptions: f.formatOptions } : {}),
      }));

      const visibleEntities =
        typeof filterEntities === 'function'
          ? filterEntities(entityOptions)
          : entityOptions;

      const groupFields = visibleEntities.map((entity) => {
        const isTeam = entity.value === 'Team';
        const nestedFields = isTeam
          ? (roleList || []).map((role) => ({
              id: role.value,
              label: role.label,
              operators: [
                { value: '=', label: 'equals' },
                { value: '!=', label: 'not equals' },
              ],
            }))
          : advancedFilterFields.map((f) => ({
              id: f.id,
              label: f.label,
              operators: f.operators || [],
              ...(f.getValueGetter ? { getValueGetter: f.getValueGetter } : {}),
              ...(f.formatOptions ? { formatOptions: f.formatOptions } : {}),
            }));

        return {
          id: entity.value,
          label: entity.label,
          isGroup: true,
          fields: nestedFields,
        };
      });

      return [...simpleFields, ...groupFields];
    }, [advancedFilterFields, entityOptions, filterEntities, roleList]);

    // allowGroups is derived directly from showAdvancedRuleButton.
    // Consumers who previously used isTemporalView should now pass
    // showAdvancedRuleButton={!isTemporalView} at the call site.
    const allowGroups = showAdvancedRuleButton;

    // -----------------------------------------------------------------------
    // Sync internal state when the drawer opens.
    //
    // FIX: deps intentionally restricted to [open].
    // We do NOT include defaultSavedFilterId or savedFilters because:
    //   - We only want to sync state when the drawer opens, not on every
    //     parent re-render that produces new array/object references.
    //   - Including savedFilters would re-run this effect whenever the parent
    //     adds/removes a filter, potentially re-selecting defaultSavedFilterId
    //     mid-session and resetting the user's in-progress edits.
    // defaultSavedFilterId and savedFilters are read inside the effect; they
    // are not tracked as reactive dependencies.
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (open) {
        setInternalBasicCriteria(basicFilterCriteria || {});
        setInternalAdvancedCriteria(
          advancedFilterCriteria || DEFAULT_ADVANCED_CRITERIA
        );
        setHasSavedFilterContext(Boolean(defaultSavedFilterId));

        if (defaultSavedFilterId && savedFilters.length > 0) {
          const match = savedFilters.find((f) => f.id === defaultSavedFilterId);
          if (match) {
            setSelectedSavedFilterId(match.id);
            setActiveTab(TAB.SAVED);
          }
        } else {
          const hasAdvanced = tabHasCriteria(
            TAB.ADVANCED,
            null,
            advancedFilterCriteria || DEFAULT_ADVANCED_CRITERIA
          );
          const hasBasic = tabHasCriteria(TAB.BASIC, basicFilterCriteria || {});

          if (hasAdvanced && !hasBasic) {
            setActiveTab(TAB.ADVANCED);
          } else if (hasBasic && !hasAdvanced) {
            setActiveTab(TAB.BASIC);
          }
        }
      }
    }, [open]); // intentional — see comment above

    // -----------------------------------------------------------------------
    // Reset on close
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (!open) {
        setActiveTab(initialTab);
        setConfirmDialogOpen(false);
        setConfirmContext(null);
        setDeleteConfirmOpen(false);
        setFilterToDelete(null);
        setSaveFilterDialogOpen(false);
        setSaveFilterName('');
        setSaveFilterDescription('');
        setEditingFilter(null);
        setSavedFiltersSearchQuery('');
        setSelectedBasicField(null);
        setBasicFieldSearchQuery('');
        setSelectedSavedFilterId(null);
        setHasSavedFilterContext(false);
      }
    }, [open, initialTab]);

    // Auto-select first basic field
    useEffect(() => {
      if (
        open &&
        activeTab === TAB.BASIC &&
        basicFilterFields.length > 0 &&
        !selectedBasicField
      ) {
        setSelectedBasicField(basicFilterFields[0].id);
      }
    }, [open, activeTab, basicFilterFields, selectedBasicField]);

    useEffect(() => {
      setBasicFieldSearchQuery('');
    }, [selectedBasicField]);

    // -----------------------------------------------------------------------
    // Confirm dialog helpers
    // -----------------------------------------------------------------------
    const openConfirmForTabSwitch = useCallback((destinationTab) => {
      setConfirmContext({ type: 'tab-switch', pendingTab: destinationTab });
      setConfirmDialogOpen(true);
    }, []);

    const openConfirmForClose = useCallback(() => {
      setConfirmContext({ type: 'close' });
      setConfirmDialogOpen(true);
    }, []);

    // -----------------------------------------------------------------------
    // clearFilter — exposed via ref
    // -----------------------------------------------------------------------
    const clearFilter = useCallback(() => {
      setInternalBasicCriteria({});
      setInternalAdvancedCriteria(DEFAULT_ADVANCED_CRITERIA);
      setEditingFilter(null);
      setSelectedSavedFilterId(null);
      setHasSavedFilterContext(false);
      onBasicFilterChange?.({});
      onAdvancedFilterChange?.(DEFAULT_ADVANCED_CRITERIA);
      onReset?.();
    }, [onBasicFilterChange, onAdvancedFilterChange, onReset]);

    useImperativeHandle(ref, () => ({ clearFilter }), [clearFilter]);

    // -----------------------------------------------------------------------
    // Confirm action
    // -----------------------------------------------------------------------
    const handleConfirmAction = useCallback(() => {
      if (!confirmContext) return;

      if (confirmContext.type === 'tab-switch') {
        const departingTab = activeTab;
        const pendingTab = confirmContext.pendingTab;

        // Close the dialog FIRST — before firing any callbacks that could
        // trigger parent re-renders (e.g. onReset closing the drawer),
        // which would race against setActiveTab below.
        setConfirmDialogOpen(false);
        setConfirmContext(null);

        if (departingTab === TAB.SAVED) {
          // Do NOT call clearFilter() here — clearFilter calls onReset which
          // is intended for explicit user-initiated resets. Calling it during
          // a tab switch can cause the parent to close the drawer or flip
          // `open` to false, triggering the useEffect(!open) cleanup that
          // resets activeTab back to initialTab before setActiveTab(pendingTab)
          // can take effect. Instead, inline only the internal state we need.
          setInternalBasicCriteria({});
          setInternalAdvancedCriteria(DEFAULT_ADVANCED_CRITERIA);
          setSelectedSavedFilterId(null);
          setHasSavedFilterContext(false);
          onBasicFilterChange?.({});
          onAdvancedFilterChange?.(DEFAULT_ADVANCED_CRITERIA);
          onReset?.();
        } else if (departingTab === TAB.BASIC) {
          setInternalBasicCriteria({});
          onBasicFilterChange?.({});
        } else if (departingTab === TAB.ADVANCED) {
          setInternalAdvancedCriteria(DEFAULT_ADVANCED_CRITERIA);
          onAdvancedFilterChange?.(DEFAULT_ADVANCED_CRITERIA);
        }

        setEditingFilter(null);
        setHasSavedFilterContext(false);
        setActiveTab(pendingTab);
        // Early return — dialog state already cleared above
        return;
      }

      if (confirmContext.type === 'close') {
        setEditingFilter(null);
        onClose?.();
      }

      setConfirmDialogOpen(false);
      setConfirmContext(null);
    }, [
      confirmContext,
      activeTab,
      onBasicFilterChange,
      onAdvancedFilterChange,
      onReset,
      onClose,
    ]);

    const handleCancelConfirm = useCallback(() => {
      setConfirmDialogOpen(false);
      setConfirmContext(null);
    }, []);

    const confirmTitle = useMemo(() => {
      if (!confirmContext) return '';
      if (confirmContext.type === 'close') return 'Discard unsaved changes?';
      if (activeTab === TAB.SAVED) return 'Applied filter will be cleared';
      return 'Unsaved filters will be cleared';
    }, [confirmContext, activeTab]);

    const confirmBody = useMemo(() => {
      if (!confirmContext) return '';
      if (confirmContext.type === 'close') {
        return `You're currently editing "${editingFilter?.name}". Closing the drawer will discard your changes.`;
      }
      if (activeTab === TAB.SAVED) {
        const filterName = savedFilters.find(
          (f) => f.id === selectedSavedFilterId
        )?.name;
        return `"${filterName || 'This filter'}" will be deselected and the applied filter will be cleared. This cannot be undone.`;
      }
      return `Your ${TAB_LABELS[activeTab]} filter selections will be lost if you switch tabs. This cannot be undone.`;
    }, [
      confirmContext,
      activeTab,
      editingFilter,
      savedFilters,
      selectedSavedFilterId,
    ]);

    const confirmActionLabel = useMemo(() => {
      if (!confirmContext) return 'Continue';
      if (confirmContext.type === 'close') return 'Discard & Close';
      return 'Clear & Switch';
    }, [confirmContext]);

    // -----------------------------------------------------------------------
    // Drawer close
    // -----------------------------------------------------------------------
    const handleDrawerClose = useCallback(() => {
      if (editingFilter) {
        openConfirmForClose();
        return;
      }
      onClose?.();
    }, [editingFilter, openConfirmForClose, onClose]);

    // -----------------------------------------------------------------------
    // Tab change
    // -----------------------------------------------------------------------
    const handleTabChange = useCallback(
      (newValue) => {
        const needsConfirm =
          tabHasCriteria(
            activeTab,
            internalBasicCriteria,
            internalAdvancedCriteria,
            selectedSavedFilterId,
            hasSavedFilterContext
          ) || Boolean(editingFilter);

        if (!needsConfirm) {
          setActiveTab(newValue);
          setEditingFilter(null);
          return;
        }

        openConfirmForTabSwitch(newValue);
      },
      [
        activeTab,
        internalBasicCriteria,
        internalAdvancedCriteria,
        selectedSavedFilterId,
        hasSavedFilterContext,
        editingFilter,
        openConfirmForTabSwitch,
      ]
    );

    // -----------------------------------------------------------------------
    // Advanced filter change
    // -----------------------------------------------------------------------
    const handleAdvancedFilterChange = useCallback(
      (newCriteria) => {
        setInternalAdvancedCriteria(newCriteria);
        onAdvancedFilterChange?.(newCriteria);
      },
      [onAdvancedFilterChange]
    );

    // -----------------------------------------------------------------------
    // Footer button state
    // -----------------------------------------------------------------------
    const advancedSort = internalAdvancedCriteria?.sort;
    const isSortValid = Boolean(advancedSort?.fieldId && advancedSort?.order);

    const canSaveFilter = useMemo(() => {
      if (activeTab === TAB.BASIC) {
        const allRules = flattenRules(
          Object.entries(internalBasicCriteria).map(([field, value]) => ({
            field,
            value,
          }))
        );
        return (
          allRules.length > 0 && allRules.every((r) => !isValueEmpty(r.value))
        );
      }
      if (activeTab === TAB.ADVANCED) {
        const allRules = flattenRules(internalAdvancedCriteria?.rules || []);
        const hasValidRules =
          allRules.length > 0 && allRules.every((r) => !isValueEmpty(r.value));
        return hasValidRules || isSortValid;
      }
      return false;
    }, [
      activeTab,
      internalBasicCriteria,
      internalAdvancedCriteria,
      isSortValid,
    ]);

    const canResetFilter = useMemo(() => {
      if (activeTab === TAB.BASIC) {
        return Object.values(internalBasicCriteria).some(
          (v) => !isValueEmpty(v)
        );
      }
      if (activeTab === TAB.ADVANCED) {
        const allRules = flattenRules(internalAdvancedCriteria?.rules || []);
        return (
          allRules.some((r) => !isValueEmpty(r.value)) || Boolean(advancedSort)
        );
      }
      return false;
    }, [
      activeTab,
      internalBasicCriteria,
      internalAdvancedCriteria,
      advancedSort,
    ]);

    const isAnyFilterValueEmpty = useMemo(() => {
      if (activeTab !== TAB.ADVANCED) return false;
      const allRules = flattenRules(internalAdvancedCriteria?.rules || []);
      return allRules.some((r) => isValueEmpty(r.value));
    }, [activeTab, internalAdvancedCriteria]);

    const activeBasicFiltersCount = useMemo(
      () =>
        Object.values(internalBasicCriteria).filter((v) => !isValueEmpty(v))
          .length,
      [internalBasicCriteria]
    );

    const activeAdvancedRulesCount = useMemo(() => {
      const allRules = flattenRules(internalAdvancedCriteria?.rules || []);
      return allRules.filter((r) => !isValueEmpty(r.value)).length;
    }, [internalAdvancedCriteria]);

    // -----------------------------------------------------------------------
    // Apply
    // -----------------------------------------------------------------------
    const handleApply = useCallback(async () => {
      if (activeTab === TAB.ADVANCED && isAnyFilterValueEmpty) {
        onValidationWarning?.(
          'Please fill all the fields to apply the filter.'
        );
        return;
      }

      const isBasic = activeTab === TAB.BASIC;

      const query = {
        mode: isBasic ? 'basic' : 'advanced',
        logic: isBasic
          ? 'or'
          : (internalAdvancedCriteria?.logic || 'AND').toLowerCase(),
        rules: isBasic
          ? Object.entries(internalBasicCriteria).map(([field, value]) => ({
              field,
              value,
            }))
          : internalAdvancedCriteria?.rules || [],
        sort: isBasic ? null : internalAdvancedCriteria?.sort || null,
      };

      if (editingFilter) {
        // Editing a saved filter: persist the update first, then fire onApply
        // with the savedFilter key so the outer chip updates immediately.
        const type = isBasic ? 'basic' : 'advanced';
        const filterData = {
          name: editingFilter.name,
          description: editingFilter.description || '',
          type,
          criteria:
            type === 'basic' ? internalBasicCriteria : internalAdvancedCriteria,
          isFavorite: editingFilter.isFavorite || false,
        };

        try {
          await onUpdateFilter?.(editingFilter.id, filterData);
        } catch (error) {
          console.error('Error updating filter:', error);
          return;
        }

        onApply?.({
          ...query,
          savedFilter: { id: editingFilter.id, name: editingFilter.name },
        });
        setEditingFilter(null);
      } else {
        // FIX: always include savedFilter key so the output shape is consistent.
        // SolyticsAppliedFilterCard and consumers rely on this key being present (even
        // when null) to distinguish manually built filters from saved filters.
        onApply?.({ ...query, savedFilter: null });
      }

      onClose?.();
    }, [
      activeTab,
      isAnyFilterValueEmpty,
      internalBasicCriteria,
      internalAdvancedCriteria,
      editingFilter,
      onApply,
      onClose,
      onUpdateFilter,
      onValidationWarning,
    ]);

    const handleReset = useCallback(() => {
      clearFilter();
    }, [clearFilter]);

    // -----------------------------------------------------------------------
    // Basic filter handlers
    // -----------------------------------------------------------------------
    const handleBasicFilterChange = useCallback(
      (fieldId, value) => {
        const newCriteria = { ...internalBasicCriteria };
        if (isValueEmpty(value)) {
          delete newCriteria[fieldId];
        } else {
          newCriteria[fieldId] = value;
        }
        setInternalBasicCriteria(newCriteria);
        onBasicFilterChange?.(newCriteria);
      },
      [internalBasicCriteria, onBasicFilterChange]
    );

    const handleClearBasicField = useCallback(
      (fieldId, e) => {
        e?.stopPropagation();
        handleBasicFilterChange(fieldId, null);
      },
      [handleBasicFilterChange]
    );

    // -----------------------------------------------------------------------
    // Saved filter handlers
    // -----------------------------------------------------------------------
    const handleApplySavedFilter = useCallback(
      (filter) => {
        setSelectedSavedFilterId(filter.id);

        if (filter?.type === 'basic') {
          const criteria = filter?.criteria || {};
          setInternalBasicCriteria(criteria);
          setInternalAdvancedCriteria(DEFAULT_ADVANCED_CRITERIA);
          setActiveTab(TAB.BASIC);
          onBasicFilterChange?.(criteria);
          onApply?.({
            mode: 'basic',
            logic: 'or',
            rules: Object.entries(criteria).map(([field, value]) => ({
              field,
              value,
            })),
            sort: null,
            savedFilter: { id: filter?.id, name: filter?.name },
          });
        } else {
          const criteria = filter?.criteria || DEFAULT_ADVANCED_CRITERIA;
          setInternalAdvancedCriteria(criteria);
          setInternalBasicCriteria({});
          setActiveTab(TAB.ADVANCED);
          onAdvancedFilterChange?.(criteria);
          onApply?.({
            mode: 'advanced',
            logic: (criteria.logic || 'AND').toLowerCase(),
            rules: criteria.rules || [],
            sort: criteria.sort || null,
            savedFilter: { id: filter?.id, name: filter?.name },
          });
        }
      },
      [onBasicFilterChange, onAdvancedFilterChange, onApply]
    );

    const handleEditSavedFilter = useCallback((filter) => {
      setEditingFilter(filter);
      setSaveFilterName(filter?.name || '');
      setSaveFilterDescription(filter?.description || '');

      if (filter?.type === 'basic') {
        setInternalBasicCriteria(filter?.criteria || {});
        setInternalAdvancedCriteria(DEFAULT_ADVANCED_CRITERIA);
        setActiveTab(TAB.BASIC);
      } else {
        setInternalAdvancedCriteria(
          filter?.criteria || DEFAULT_ADVANCED_CRITERIA
        );
        setInternalBasicCriteria({});
        setActiveTab(TAB.ADVANCED);
      }
    }, []);

    const handleOpenMetadataModal = useCallback(() => {
      setSaveFilterDialogOpen(true);
    }, []);

    const handleDeleteSavedFilter = useCallback((filterId) => {
      setFilterToDelete(filterId);
      setDeleteConfirmOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (!filterToDelete) return;
      try {
        await onDeleteFilter?.(filterToDelete);
        if (selectedSavedFilterId === filterToDelete)
          setSelectedSavedFilterId(null);
      } catch (error) {
        console.error('Error deleting filter:', error);
      } finally {
        setDeleteConfirmOpen(false);
        setFilterToDelete(null);
      }
    }, [onDeleteFilter, selectedSavedFilterId, filterToDelete]);

    const handleToggleFavorite = useCallback(
      async (filterId) => {
        try {
          await onToggleFavorite?.(filterId);
        } catch (error) {
          console.error('Error toggling favourite:', error);
        }
      },
      [onToggleFavorite]
    );

    // -----------------------------------------------------------------------
    // Save / update filter modal
    // -----------------------------------------------------------------------
    const handleOpenSaveDialog = useCallback(() => {
      setSaveFilterDialogOpen(true);
    }, []);

    // FIX: reset name and description when the modal is closed without saving,
    // so stale text is never shown when the modal is reopened.
    const handleCloseSaveDialog = useCallback(() => {
      setSaveFilterDialogOpen(false);
      setSaveFilterName('');
      setSaveFilterDescription('');
    }, []);

    const handleConfirmSaveFilter = useCallback(async () => {
      if (!saveFilterName.trim()) return;

      const isEditing = Boolean(editingFilter);
      const type = activeTab === TAB.BASIC ? 'basic' : 'advanced';

      const filterData = {
        name: saveFilterName.trim(),
        description: saveFilterDescription.trim(),
        type,
        criteria:
          type === 'basic' ? internalBasicCriteria : internalAdvancedCriteria,
        isFavorite: editingFilter?.isFavorite || false,
      };

      try {
        if (isEditing) {
          await onUpdateFilter?.(editingFilter.id, filterData);
          setEditingFilter({ ...editingFilter, ...filterData });

          if (editingFilter.id === selectedSavedFilterId) {
            onApply?.({
              mode: type,
              logic:
                type === 'basic'
                  ? 'or'
                  : (internalAdvancedCriteria?.logic || 'AND').toLowerCase(),
              rules:
                type === 'basic'
                  ? Object.entries(internalBasicCriteria).map(
                      ([field, value]) => ({ field, value })
                    )
                  : internalAdvancedCriteria?.rules || [],
              sort:
                type === 'basic'
                  ? null
                  : internalAdvancedCriteria?.sort || null,
              savedFilter: { id: editingFilter.id, name: filterData.name },
            });
          }
        } else {
          await onSaveFilter?.(filterData);
          setEditingFilter(null);
        }

        setSaveFilterName('');
        setSaveFilterDescription('');
        setSaveFilterDialogOpen(false);

        if (showSavedFilters && !isEditing) {
          setActiveTab(TAB.SAVED);
        }
      } catch (error) {
        console.error('Error saving filter:', error);
      }
    }, [
      saveFilterName,
      saveFilterDescription,
      activeTab,
      internalBasicCriteria,
      internalAdvancedCriteria,
      editingFilter,
      onSaveFilter,
      onUpdateFilter,
      showSavedFilters,
      onApply,
      selectedSavedFilterId,
    ]);

    // -----------------------------------------------------------------------
    // Sorted saved filters
    // -----------------------------------------------------------------------
    const sortedSavedFilters = useMemo(() => {
      const query = savedFiltersSearchQuery.trim().toLowerCase();
      const filtered = query
        ? savedFilters.filter(
            (f) =>
              f.name.toLowerCase().includes(query) ||
              (f.description && f.description.toLowerCase().includes(query))
          )
        : savedFilters;

      return [...filtered].sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        return a.name.localeCompare(b.name);
      });
    }, [savedFilters, savedFiltersSearchQuery]);

    const getBasicFieldSelectedCount = useCallback(
      (fieldId) => {
        const value = internalBasicCriteria[fieldId];
        if (isValueEmpty(value)) return 0;
        if (Array.isArray(value)) return value.length;
        return 1;
      },
      [internalBasicCriteria]
    );

    // -----------------------------------------------------------------------
    // Basic filter value input
    // -----------------------------------------------------------------------
    const renderBasicFilterInput = useCallback(
      (field) => {
        const fieldValue = internalBasicCriteria[field.id];

        if (field.selectionMode === 'text') {
          return (
            <SolyticsGrid
              sx={{ flexDirection: 'column', spacing: 2, paddingRight: 2 }}
            >
              <SolyticsLabel label="Value" />
              <SolyticsInput
                name={`basic-filter-${field.id}`}
                value={fieldValue || ''}
                onChange={(e) =>
                  handleBasicFilterChange(field.id, e.target.value || null)
                }
                placeholder="Enter a value..."
                multiline
                minRows={4}
                fullWidth
              />
            </SolyticsGrid>
          );
        }

        if (field.selectionMode === 'multiselect') {
          const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
          const options = field.options || [];
          const filteredOptions = options.filter((option) => {
            if (!basicFieldSearchQuery) return true;
            const label =
              typeof option === 'string'
                ? option
                : option.label || option.name || '';
            return label
              .toLowerCase()
              .includes(basicFieldSearchQuery.toLowerCase());
          });

          return (
            <SolyticsBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <SolyticsBox>
                <SolyticsLabel label="Value" />
                <SolyticsBox sx={{ pr: 2 }}>
                  <SolyticsSearchBar
                    name={`basic-filter-search-${field.id}`}
                    placeholder={`Search ${field.label}...`}
                    value={basicFieldSearchQuery}
                    onChange={(e) => setBasicFieldSearchQuery(e.target.value)}
                    fullWidth
                    size="small"
                    ariaDescription={`Search ${field.label}`}
                  />
                </SolyticsBox>
              </SolyticsBox>
              <SolyticsBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflowY: 'auto',
                  pr: 2,
                }}
              >
                {filteredOptions.length === 0 ? (
                  <SolyticsBox sx={{ p: 2, textAlign: 'center' }}>
                    <SolyticsTypography variant="body2" color="text.secondary">
                      No options found
                    </SolyticsTypography>
                  </SolyticsBox>
                ) : (
                  <SolyticsList
                    items={filteredOptions.map((option) => {
                      const optionValue =
                        typeof option === 'string'
                          ? option
                          : option.value || option.id;
                      const optionLabel =
                        typeof option === 'string'
                          ? option
                          : option.label || option.name || '';
                      const isChecked = selectedValues.includes(optionValue);

                      return {
                        id: optionValue,
                        label: optionLabel,
                        checked: isChecked,
                        onClick: () => {
                          const newValues = isChecked
                            ? selectedValues.filter((v) => v !== optionValue)
                            : [...selectedValues, optionValue];
                          handleBasicFilterChange(
                            field.id,
                            newValues.length > 0 ? newValues : null
                          );
                        },
                      };
                    })}
                  />
                )}
              </SolyticsBox>
            </SolyticsBox>
          );
        }

        const options = field.options || [];
        return (
          <SolyticsBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              pr: 2,
            }}
          >
            <SolyticsLabel label="Value" />
            <SolyticsAutocomplete
              name={`basic-filter-${field.id}`}
              multiple={false}
              options={options}
              value={fieldValue || null}
              onChange={(value) => handleBasicFilterChange(field.id, value)}
              placeholder={`Select ${field.label.toLowerCase()}...`}
              controlled={!!field.getValueGetter}
              fetchData={field.getValueGetter}
              formatOptions={field.formatOptions}
            />
          </SolyticsBox>
        );
      },
      [internalBasicCriteria, basicFieldSearchQuery, handleBasicFilterChange]
    );

    // -----------------------------------------------------------------------
    // Basic filter tabs (incorporating content)
    // -----------------------------------------------------------------------
    const renderBasicFilterTabs = useCallback(() => {
      if (basicFilterFields.length === 0) {
        return (
          <SolyticsTypography variant="body1" color="text.secondary">
            No basic filter fields configured.
          </SolyticsTypography>
        );
      }

      const verticalTabItems = basicFilterFields.map((field) => {
        const selectedCount = getBasicFieldSelectedCount(field.id);
        const hasSelection = selectedCount > 0;

        return {
          value: field.id,
          label: (
            <SolyticsBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                overflow: 'hidden',
                gap: '8px',
                maxWidth: '120px',
              }}
            >
              <SolyticsBox
                sx={{
                  flex: 1,
                  textAlign: 'left',
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                <SolyticsTypography variant="body1" noWrap ellipsis>
                  {field.label}
                </SolyticsTypography>
              </SolyticsBox>
              {hasSelection && (
                <SolyticsBox
                  sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <SolyticsBox
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      borderRadius: '10px',
                      minWidth: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      // padding: '0 6px',
                      fontSize: '12px',
                      fontWeight: theme.typography.fontWeightMedium,
                      lineHeight: 1,
                    }}
                  >
                    {selectedCount}
                  </SolyticsBox>
                  <SolyticsIconButton
                    size="small"
                    onClick={(e) => handleClearBasicField(field.id, e)}
                    aria-label={`Clear ${field.label}`}
                    disablePadding
                  >
                    <CloseIcon />
                  </SolyticsIconButton>
                </SolyticsBox>
              )}
            </SolyticsBox>
          ),
          content: renderBasicFilterInput(field),
        };
      });

      return (
        <SolyticsTabs
          variant="highlighted_1"
          orientation="vertical"
          size="medium"
          value={selectedBasicField || basicFilterFields[0]?.id}
          onChange={(newValue) => setSelectedBasicField(newValue)}
          items={verticalTabItems}
          separator={true}
        />
      );
    }, [
      basicFilterFields,
      selectedBasicField,
      getBasicFieldSelectedCount,
      handleClearBasicField,
      theme,
      renderBasicFilterInput,
    ]);

    // -----------------------------------------------------------------------
    // Tab badge / label
    // -----------------------------------------------------------------------
    const tabBadge = useCallback(
      (count) =>
        count > 0 ? (
          <SolyticsBox
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: '10px',
              minWidth: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
              fontSize: '12px',
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1,
            }}
          >
            {count}
          </SolyticsBox>
        ) : null,
      [theme]
    );

    const tabLabel = useCallback(
      (text, count) => (
        <SolyticsBox sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SolyticsTypography variant="body1">{text}</SolyticsTypography>
          {tabBadge(count)}
        </SolyticsBox>
      ),
      [tabBadge]
    );

    // -----------------------------------------------------------------------
    // Footer button labels
    // -----------------------------------------------------------------------
    const applyButtonLabel = editingFilter ? 'Update & Apply' : 'Apply';
    const saveButtonLabel = editingFilter ? 'Update Filter' : 'Save Filter';
    const saveButtonTitle = canSaveFilter
      ? saveButtonLabel
      : 'No filters to save';

    // -----------------------------------------------------------------------
    // Tab items
    // -----------------------------------------------------------------------
    const tabItems = useMemo(() => {
      const items = [];

      if (showBasicFilters) {
        items.push({
          label: tabLabel('Basic Filters', activeBasicFiltersCount),
          value: TAB.BASIC,
          content: (
            <SolyticsBox
              sx={{
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                p: 2,
                pr: 0,
              }}
            >
              {editingFilter && activeTab === TAB.BASIC && (
                <EditingBanner
                  filterName={editingFilter.name}
                  onEditMetadata={handleOpenMetadataModal}
                  theme={theme}
                />
              )}
              <SolyticsBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                {loading ? (
                  <>
                    <SolyticsBox
                      sx={{
                        width: '240px',
                        borderRight: `1px solid ${theme.palette.grey?.[60] || theme.palette.divider}`,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {Array.from({ length: skeletonCounts.leftColumn }).map(
                        (_, i) => (
                          <Skeleton
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            variant="rectangular"
                            height={40}
                            sx={{ borderRadius: 1, flexShrink: 0 }}
                          />
                        )
                      )}
                    </SolyticsBox>
                    <SolyticsBox
                      sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        gap: '16px',
                        p: 2,
                      }}
                    >
                      <SolyticsBox>
                        <Skeleton
                          variant="rounded"
                          height={40}
                          sx={{ borderRadius: 1, mt: '6px' }}
                        />
                      </SolyticsBox>
                      <SolyticsBox
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        {Array.from({ length: skeletonCounts.rightColumn }).map(
                          (_, i) => (
                            <Skeleton
                              // eslint-disable-next-line react/no-array-index-key
                              key={i}
                              variant="rectangular"
                              height={40}
                              sx={{ borderRadius: 1 }}
                            />
                          )
                        )}
                      </SolyticsBox>
                    </SolyticsBox>
                  </>
                ) : (
                  renderBasicFilterTabs()
                )}
              </SolyticsBox>
            </SolyticsBox>
          ),
        });
      }

      items.push({
        label: tabLabel('Advanced Filters', activeAdvancedRulesCount),
        value: TAB.ADVANCED,
        content: (
          <SolyticsBox
            sx={{
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              p: 2,
              pr: 0,
            }}
          >
            {editingFilter && activeTab === TAB.ADVANCED && (
              <EditingBanner
                filterName={editingFilter.name}
                onEditMetadata={handleOpenMetadataModal}
                theme={theme}
              />
            )}

            <SolyticsAdvancedFilter
              fields={advancedFilterFieldsSchema}
              value={internalAdvancedCriteria}
              onChange={handleAdvancedFilterChange}
              allowGroups={allowGroups}
              sortableFields={sortableFields}
              dataTypeOptions={dataTypeOptions}
              loading={loading}
            />
          </SolyticsBox>
        ),
      });

      if (showSavedFilters) {
        let savedFiltersBody;
        if (loading) {
          savedFiltersBody = (
            <SolyticsGrid sx={{ p: 2, pt: 0 }}>
              <Skeleton
                variant="rectangular"
                height={40}
                sx={{ mb: 3, borderRadius: 1 }}
              />
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  variant="rectangular"
                  height={68}
                  sx={{ mb: 1, borderRadius: 1 }}
                />
              ))}
            </SolyticsGrid>
          );
        } else if (sortedSavedFilters.length === 0) {
          const emptyText = savedFiltersSearchQuery
            ? 'No saved filters match your search.'
            : 'No saved filters yet.';
          savedFiltersBody = (
            <SolyticsGrid sx={{ padding: 2, pt: 0 }}>
              <SolyticsTypography variant="body2" color="text.secondary">
                {emptyText}
              </SolyticsTypography>
            </SolyticsGrid>
          );
        } else {
          savedFiltersBody = (
            <SolyticsGrid
              sx={{ overflowY: 'auto', padding: 2, pt: 0, height: '100%' }}
            >
              <SolyticsList
                disablePadding
                gap={1}
                items={sortedSavedFilters.map((filter) => {
                  const isSelected = selectedSavedFilterId === filter?.id;
                  return {
                    id: filter.id,
                    selected: isSelected,
                    onClick: () => handleApplySavedFilter(filter),
                    custom: (
                      <SolyticsBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: 2,
                          py: 1,
                          pr: 1,
                          borderRadius: 0.5,
                        }}
                      >
                        <SolyticsBox sx={{ flexShrink: 0, display: 'flex' }}>
                          <SolyticsIconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(filter?.id);
                            }}
                            aria-label={
                              filter?.isFavorite ? 'Unfavourite' : 'Favourite'
                            }
                          >
                            {filter?.isFavorite ? (
                              <StarIcon
                                sx={{
                                  color: '#FFD700',
                                  fontSize: '20px',
                                }}
                              />
                            ) : (
                              <StarIcon sx={{ fontSize: '20px' }} />
                            )}
                          </SolyticsIconButton>
                        </SolyticsBox>
                        <SolyticsBox
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          <SolyticsTooltip text={filter.name} placement="top">
                            <SolyticsGrid
                              sx={{ display: 'block', overflow: 'hidden' }}
                            >
                              <SolyticsTypography
                                variant="body1"
                                color="text.primary"
                                noWrap
                                width="min-content"
                              >
                                {filter?.name}
                              </SolyticsTypography>
                            </SolyticsGrid>
                          </SolyticsTooltip>

                          <SolyticsTooltip
                            text={filter?.description || filter?.type}
                            placement="bottom"
                          >
                            <SolyticsBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                              }}
                            >
                              <SolyticsBox sx={{ flexShrink: 0 }}>
                                <SolyticsChip
                                  label={
                                    FILTER_TYPE_LABELS[filter.type] ||
                                    filter?.type
                                  }
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 20,
                                    fontSize: '11px',
                                  }}
                                />
                              </SolyticsBox>
                              {filter?.description && (
                                <SolyticsTypography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ flex: 1, minWidth: 0 }}
                                >
                                  {filter?.description}
                                </SolyticsTypography>
                              )}
                            </SolyticsBox>
                          </SolyticsTooltip>
                        </SolyticsBox>

                        <SolyticsBox
                          sx={{
                            flexShrink: 0,
                            display: 'flex',
                            gap: '4px',
                          }}
                        >
                          <SolyticsIconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSavedFilter(filter);
                            }}
                            aria-label="Edit filter"
                          >
                            <PencilIcon
                              style={{
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          </SolyticsIconButton>
                          <SolyticsIconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSavedFilter(filter?.id);
                            }}
                            aria-label="Delete filter"
                          >
                            <TrashIcon
                              style={{
                                width: '16px',
                                height: '16px',
                              }}
                            />
                          </SolyticsIconButton>
                        </SolyticsBox>
                      </SolyticsBox>
                    ),
                  };
                })}
              />
            </SolyticsGrid>
          );
        }

        items.push({
          label: (
            <SolyticsTypography variant="body1">
              Saved Filters
            </SolyticsTypography>
          ),
          value: TAB.SAVED,
          content: (
            <SolyticsStack
              sx={{
                flexWrap: 'nowrap',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
              }}
            >
              <SolyticsGrid size={12} sx={{ p: 2 }}>
                <SolyticsSearchBar
                  name="saved-filters-search"
                  placeholder="Search saved filters..."
                  value={savedFiltersSearchQuery}
                  onChange={(e) => setSavedFiltersSearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  ariaDescription="Search saved filters"
                />
              </SolyticsGrid>
              {savedFiltersBody}
            </SolyticsStack>
          ),
        });
      }

      return items;
    }, [
      showBasicFilters,
      showSavedFilters,
      activeBasicFiltersCount,
      activeAdvancedRulesCount,
      activeTab,
      editingFilter,
      loading,
      internalAdvancedCriteria,
      savedFiltersSearchQuery,
      sortedSavedFilters,
      selectedSavedFilterId,
      advancedFilterFieldsSchema,
      allowGroups,
      sortableFields,
      dataTypeOptions,
      renderBasicFilterTabs,
      handleAdvancedFilterChange,
      handleApplySavedFilter,
      handleEditSavedFilter,
      handleDeleteSavedFilter,
      handleToggleFavorite,
      handleOpenMetadataModal,
      tabLabel,
      skeletonCounts,
      theme,
    ]);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <>
        <SolyticsDrawer
          open={open}
          disableContentPadding
          header={title}
          handleClose={handleDrawerClose}
          slotProps={{
            paper: {
              sx: {
                width: 600,
              },
            },
          }}
          aria-labelledby="filter-drawer-title"
          content={
            <SolyticsBox paddingTop={2} overflow="hidden" height="100%">
              <SolyticsTabs
                variant="highlighted_3"
                size="small"
                fullWidth
                value={activeTab}
                onChange={handleTabChange}
                items={tabItems}
                tabBarPaddingX={2}
              />
            </SolyticsBox>
          }
          footer={
            <SolyticsBox
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                flexShrink: 0,
              }}
            >
              {onSaveFilter && activeTab !== TAB.SAVED ? (
                <SolyticsButton
                  onClick={
                    editingFilter
                      ? handleOpenMetadataModal
                      : handleOpenSaveDialog
                  }
                  variant="contained"
                  disabled={!canSaveFilter}
                  title={saveButtonTitle}
                >
                  {saveButtonLabel}
                </SolyticsButton>
              ) : (
                <SolyticsBox />
              )}

              <SolyticsBox
                sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
              >
                {activeTab !== TAB.SAVED && (
                  <SolyticsButton
                    onClick={handleReset}
                    variant="outlined"
                    disabled={!canResetFilter}
                    title={
                      canResetFilter ? 'Reset Filter' : 'No filter to reset'
                    }
                    sx={{
                      height: '40px',
                      padding: '8px 16px',
                      backgroundColor: theme.palette.grey?.[40] || '#f4f4f5',
                      color: theme.palette.text.primary,
                      borderRadius: '6px',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.grey?.[40] || '#f4f4f5',
                        border: 'none',
                      },
                    }}
                  >
                    Reset
                  </SolyticsButton>
                )}

                <SolyticsButton
                  onClick={handleDrawerClose}
                  variant="contained"
                  color="secondary"
                  sx={{
                    height: '40px',
                    padding: '8px 16px',
                    backgroundColor: theme.palette.grey?.[40] || '#f4f4f5',
                    color: theme.palette.text.primary,
                    borderRadius: '6px',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.grey?.[40] || '#f4f4f5',
                      border: 'none',
                    },
                  }}
                >
                  Cancel
                </SolyticsButton>

                {activeTab !== TAB.SAVED && (
                  <SolyticsButton
                    onClick={handleApply}
                    variant="contained"
                    disabled={!canSaveFilter}
                    title={applyButtonLabel}
                    sx={{
                      height: '40px',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      minWidth: '85px',
                    }}
                  >
                    {applyButtonLabel}
                  </SolyticsButton>
                )}
              </SolyticsBox>
            </SolyticsBox>
          }
        />

        {/* Delete filter confirmation dialog */}
        <SolyticsModal
          id="delete-filter-confirm"
          open={deleteConfirmOpen}
          handleClose={() => {
            setDeleteConfirmOpen(false);
            setFilterToDelete(null);
          }}
          title="Delete saved filter?"
          secondaryHeader=""
          maxWidth="xs"
          dialogContent={
            <SolyticsTypography variant="body1" color="text.secondary">
              This filter will be permanently deleted. This cannot be undone.
            </SolyticsTypography>
          }
          actions={
            <>
              <SolyticsButton
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setFilterToDelete(null);
                }}
                variant="contained"
                color="secondary"
              >
                Cancel
              </SolyticsButton>
              <SolyticsButton
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
              >
                Delete
              </SolyticsButton>
            </>
          }
        />

        {/* Tab-switch / close confirmation dialog */}
        <SolyticsModal
          id="filter-confirm"
          open={confirmDialogOpen}
          handleClose={handleCancelConfirm}
          title={confirmTitle}
          secondaryHeader=""
          maxWidth="xs"
          dialogContent={
            <SolyticsTypography variant="body1" color="text.secondary">
              {confirmBody}
            </SolyticsTypography>
          }
          actions={
            <>
              <SolyticsButton
                onClick={handleCancelConfirm}
                variant="contained"
                color="secondary"
              >
                Cancel
              </SolyticsButton>
              <SolyticsButton onClick={handleConfirmAction} variant="contained">
                {confirmActionLabel}
              </SolyticsButton>
            </>
          }
        />

        {/* Save / Update filter metadata modal */}
        <SolyticsModal
          id="save-filter-modal"
          open={saveFilterDialogOpen}
          handleClose={handleCloseSaveDialog}
          title={editingFilter ? 'Update Filter' : 'Save Filter'}
          secondaryHeader={
            editingFilter
              ? 'Update the name or description for this saved filter.'
              : 'Save this filter to use it in the future.'
          }
          maxWidth="sm"
          isEditMode={Boolean(editingFilter)}
          actions={
            <>
              <SolyticsButton
                onClick={handleCloseSaveDialog}
                variant="contained"
                color="secondary"
              >
                Cancel
              </SolyticsButton>
              <SolyticsButton
                onClick={handleConfirmSaveFilter}
                variant="contained"
                disabled={!saveFilterName.trim()}
              >
                {editingFilter ? 'Update' : 'Save'}
              </SolyticsButton>
            </>
          }
          dialogContent={
            <SolyticsBox sx={{ width: '100%' }}>
              <SolyticsBox sx={{ mb: 2, width: '100%' }}>
                <SolyticsInput
                  name="filter-name"
                  label="Name"
                  value={saveFilterName}
                  onChange={(e) => setSaveFilterName(e.target.value)}
                  placeholder="Enter name for this filter"
                  required
                  fullWidth
                />
              </SolyticsBox>
              <SolyticsBox>
                <SolyticsInput
                  name="filter-description"
                  label="Description"
                  value={saveFilterDescription}
                  onChange={(e) => {
                    if (e.target.value.length <= 45)
                      setSaveFilterDescription(e.target.value);
                  }}
                  placeholder="Enter a description..."
                  multiline
                  minRows={3}
                  maxRows={5}
                  helperText="This is helper text"
                  fullWidth
                />
                <SolyticsBox sx={{ mt: 0.5, display: 'block' }}>
                  <SolyticsTypography variant="caption" color="text.secondary">
                    {saveFilterDescription.length}/45
                  </SolyticsTypography>
                </SolyticsBox>
              </SolyticsBox>
            </SolyticsBox>
          }
        />
      </>
    );
  }
);

SolyticsTableFilter.displayName = 'SolyticsTableFilter';

export default SolyticsTableFilter;
